import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { 
  getSeasons, 
  createSeason, 
  updateSeason, 
  deleteSeason, 
  getActiveSeason, 
  setActiveSeason as setActiveSeasonInDb,
  migrateGamesToSeason,
  checkAndFixGamesWithoutSeason
} from '../firebase/firestore';
const SeasonContext = createContext();

// Provider component
export const SeasonProvider = ({ children }) => {
  // Get user from auth context
  const { currentUser } = useAuth();
  
  // State
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch all seasons for the user
  const fetchSeasons = useCallback(async (playerIdToFix = null) => {
    if (!currentUser) {
      setSeasons([]);
      setActiveSeason(null);
      return;
    }
    
    try {
      setIsLoading(true);
      const fetchedSeasons = await getSeasons(currentUser.uid);
      setSeasons(fetchedSeasons);
      
      // Get the active season
      const active = await getActiveSeason(currentUser.uid);
      if (active) {
        setActiveSeason(active);
        
        // If we have a player ID and active season, fix any games without seasonId
        if (playerIdToFix) {
          try {
            // Fix any games without seasonId by assigning them to the active season
            const fixedGamesCount = await checkAndFixGamesWithoutSeason(
              currentUser.uid,
              playerIdToFix,
              active.id
            );
            
            if (fixedGamesCount > 0) {
              console.log(`Fixed ${fixedGamesCount} games without seasonId`);
            }
          } catch (fixError) {
            console.error('Error fixing games without seasonId:', fixError);
          }
        }
      } else if (fetchedSeasons.length > 0) {
        // If no active season is set but seasons exist, use the first one
        setActiveSeason(fetchedSeasons[0]);
      }
    } catch (err) {
      console.error('Error fetching seasons:', err);
      setError('Failed to load seasons. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, setSeasons, setActiveSeason, setIsLoading, setError]);
  
  // Add a new season
  const addSeason = useCallback(async (seasonData) => {
    if (!currentUser) {
      setError('No user logged in');
      return null;
    }
    
    try {
      setIsLoading(true);
      
      // If this is the first season, mark it as active
      if (seasons.length === 0) {
        seasonData.isActive = true;
      }
      
      const seasonId = await createSeason(currentUser.uid, seasonData);
      await fetchSeasons(); // Refresh the list of seasons
      
      return seasonId;
    } catch (err) {
      console.error('Error adding season:', err);
      setError('Failed to add season. Please try again.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, seasons.length, fetchSeasons, setError, setIsLoading]);
  
  // Update a season
  const updateSeasonData = useCallback(async (seasonId, seasonData) => {
    if (!currentUser) {
      setError('No user logged in');
      return;
    }
    
    try {
      setIsLoading(true);
      await updateSeason(currentUser.uid, seasonId, seasonData);
      
      // If setting as active, update the active season in context
      if (seasonData.isActive) {
        const updatedSeasons = seasons.map(season => ({
          ...season,
          isActive: season.id === seasonId
        }));
        setSeasons(updatedSeasons);
        
        const updatedActiveSeason = updatedSeasons.find(season => season.id === seasonId);
        if (updatedActiveSeason) {
          setActiveSeason(updatedActiveSeason);
        }
      } else {
        await fetchSeasons(); // Refresh the list of seasons
      }
    } catch (err) {
      console.error('Error updating season:', err);
      setError('Failed to update season. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, seasons, fetchSeasons, setError, setIsLoading, setSeasons, setActiveSeason]);
  // Delete a season
  const removeSeason = useCallback(async (seasonId) => {
    if (!currentUser) {
      setError('No user logged in');
      return { success: false, error: 'No user logged in' };
    }

    // Check if trying to delete the active season
    if (activeSeason && activeSeason.id === seasonId) {
      const otherSeasons = seasons.filter(s => s.id !== seasonId);

      if (otherSeasons.length > 0) {
        // There are other seasons - suggest changing active season first
        setError('Cannot delete the active season. Please select a different season first, then try again.');
        return { success: false, error: 'Cannot delete active season' };
      } else {
        // This is the only season - allow deletion (will reset to no active season)
        console.log('Deleting the only season - will reset to no active season');
      }
    }

    try {
      setIsLoading(true);
      await deleteSeason(currentUser.uid, seasonId);
      await fetchSeasons(); // Refresh the list of seasons
      return { success: true };
    } catch (err) {
      console.error('Error deleting season:', err);
      setError('Failed to delete season. Please try again.');
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, activeSeason, seasons, fetchSeasons, setError, setIsLoading]);
  // Set the active season
  const changeActiveSeason = useCallback(async (seasonId) => {
    if (!currentUser) {
      setError('No user logged in');
      return;
    }
    
    try {
      setIsLoading(true);
      await setActiveSeasonInDb(currentUser.uid, seasonId);
      
      // Update the local state to reflect the change
      const updatedSeasons = seasons.map(season => ({
        ...season,
        isActive: season.id === seasonId
      }));
      
      setSeasons(updatedSeasons);
      
      const newActiveSeason = updatedSeasons.find(season => season.id === seasonId);
      if (newActiveSeason) {
        setActiveSeason(newActiveSeason);
      }
    } catch (err) {
      console.error('Error setting active season:', err);
      setError('Failed to set active season. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, seasons, setError, setIsLoading, setSeasons, setActiveSeason]);
  // Migrate games from no season to a specific season
  const migrateGames = useCallback(async (playerId, seasonId) => {
    if (!currentUser) {
      setError('No user logged in');
      return 0;
    }
    
    try {
      setIsLoading(true);
      const migratedCount = await migrateGamesToSeason(currentUser.uid, playerId, seasonId);
      return migratedCount;
    } catch (err) {
      console.error('Error migrating games:', err);
      setError('Failed to migrate games. Please try again.');
      return 0;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, setError, setIsLoading]);
  const resetSeasonState = useCallback(() => {
    setSeasons([]);
    setActiveSeason(null);
    setIsLoading(false);
    setError(null);
  }, []);
  // Fix games without seasonId
  const fixGamesWithoutSeason = useCallback(async (playerId) => {
    if (!currentUser || !activeSeason || !playerId) return 0;
    
    try {
      return await checkAndFixGamesWithoutSeason(
        currentUser.uid,
        playerId,
        activeSeason.id
      );
    } catch (error) {
      console.error('Error fixing games without seasonId:', error);
      return 0;
    }
  }, [currentUser, activeSeason]);
  
  // Effect to fetch seasons when the user changes
  useEffect(() => {
    if (currentUser) {
      fetchSeasons();
    } else {
      resetSeasonState();
    }
  }, [currentUser, fetchSeasons, resetSeasonState]);
  // Context value - memoized to prevent unnecessary re-renders
  const value = useMemo(() => ({
    seasons,
    activeSeason,
    isLoading,
    error,
    fetchSeasons,
    addSeason,
    updateSeasonData,
    removeSeason,
    changeActiveSeason,
    migrateGames,
    resetSeasonState,
    fixGamesWithoutSeason
  }), [
    seasons,
    activeSeason,
    isLoading,
    error,
    fetchSeasons,
    addSeason,
    updateSeasonData,
    removeSeason,
    changeActiveSeason,
    migrateGames,
    resetSeasonState,
    fixGamesWithoutSeason
  ]);
  return (
    <SeasonContext.Provider value={value}>
      {children}
    </SeasonContext.Provider>
  );
};

// Custom hook to use the season context
export const useSeason = () => {
  const context = useContext(SeasonContext);
  if (!context) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return context;
};

